import type { TableHeaderProps } from "@src/components/ui-kit/DataTable/Body/components/header";
import {
  getColumnMeta,
  getNextOrder,
} from "@src/components/ui-kit/DataTable/Body/helpers";
import { ACTIONS_COLUMN_ID } from "@src/components/ui-kit/DataTable/Body/hooks/columns";
import type {
  BodyColumnDef,
  BodyRow,
} from "@src/components/ui-kit/DataTable/types";
import { flexRender, type Header } from "@tanstack/react-table";

interface UseTableHeaderGroupArgs<Row extends BodyRow, OrderByColumn>
  extends Pick<
    TableHeaderProps<Row, OrderByColumn>,
    | "enableColumnOrdering"
    | "enableColumnResizing"
    | "shouldShowColumnVisibilityControl"
    | "columnVisibilityControlWithAddon"
    | "dataColumnIdsSet"
    | "orderedBy"
    | "onOrderByChange"
  > {
  header: Header<Row, unknown>;
}
export function useTableHeaderGroup<Row extends BodyRow, OrderByColumn>({
  header,
  enableColumnOrdering,
  enableColumnResizing,
  shouldShowColumnVisibilityControl,
  columnVisibilityControlWithAddon,
  dataColumnIdsSet,
  orderedBy,
  onOrderByChange,
}: UseTableHeaderGroupArgs<Row, OrderByColumn>) {
  const columnDef = header.column.columnDef as BodyColumnDef<
    Row,
    OrderByColumn
  >;
  const orderByColumn = columnDef.orderBy;
  const isDataColumn = dataColumnIdsSet.has(header.column.id);
  const isReorderable =
    enableColumnOrdering &&
    isDataColumn &&
    !getColumnMeta(header.column.columnDef)?.isReorderDisabled &&
    header.column.id !== ACTIONS_COLUMN_ID &&
    header.column.getIsPinned() === false;

  const headerContent =
    shouldShowColumnVisibilityControl && header.column.id === ACTIONS_COLUMN_ID
      ? columnVisibilityControlWithAddon
      : flexRender(header.column.columnDef.header, header.getContext());

  const isSortable =
    orderByColumn !== undefined && onOrderByChange !== undefined;
  const isSortedColumn = isSortable && orderedBy?.column === orderByColumn;
  const activeOrder = isSortedColumn ? orderedBy?.order : undefined;
  const showResizeHandle = enableColumnResizing && header.column.getCanResize();

  const handleOrderByChange = () => {
    if (orderByColumn === undefined || onOrderByChange === undefined) {
      return;
    }

    onOrderByChange({
      column: orderByColumn,
      order: getNextOrder(activeOrder),
    });
  };

  return {
    isReorderable,
    activeOrder,
    headerContent,
    isSortable,
    showResizeHandle,
    handleOrderByChange,
  };
}
